import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';

import ActionButton from '../../../../ActionButton';
import ActionMenu from '../../../../ActionMenu';
import ConfirmActionPopup from '../../../../ConfirmActionPopup';
import Loader from '../../../../Loader';
import { ReactComponent as AddIcon } from '../../../../../assetsNew/icons/add.svg';
import { ReactComponent as MoreIcon } from '../../../../../assetsNew/icons/more-vert.svg';
import { setMessage } from '../../../../../sagas/layout/actions';
import { openBatchMessageTemplate } from '../../../../../sagas/batchMessage/actions';
import { getBatchMessageTemplatesRequest,
  batchMessageTemplatesSetPage,
  getBatchMessageTemplatesReset
} from '../../../../../sagas/sendbird/templates/list/actions';
import { getBatchMessageTemplateRequest } from '../../../../../sagas/sendbird/templates/update/actions';
import { removeBatchMessageTemplateRequest } from '../../../../../sagas/sendbird/templates/remove/actions';

import './_batchMessageTemplates.scss';

const BatchMessageTemplates = ({ insertTemplate }) => {
  const dispatch = useDispatch();
  const [templates, setTemplates] = useState([]);
  const [templateMenu, setTemplateMenu] = useState({ anchorEl: null, item: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, item: null });
  const { dataTable, curr_page, next, loading } = useSelector(s => s.getBatchMessageTemplates);

  useEffect(() => {
    return function cancel() {
      dispatch(getBatchMessageTemplatesReset());
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    dispatch(getBatchMessageTemplatesRequest({ curr_page, signal: abortController.signal }));

    return function cancel() {
      abortController.abort('Request cancelled due to new request');
    };
  }, [curr_page]);

  useEffect(() => {
    if (curr_page <= 1) {
      setTemplates(dataTable);
    } else {
      setTemplates([...templates, ...dataTable]);
    }
  }, [dataTable]); // eslint-disable-line

  const loadMore = () => {
    dispatch(batchMessageTemplatesSetPage(curr_page + 1));
  };

  const openTemplateMenu = (anchorEl, item) => setTemplateMenu({ anchorEl, item });
  const closeTemplateMenu = () => setTemplateMenu({ anchorEl: null, item: null });

  const openTemplatePopup = () => dispatch(openBatchMessageTemplate());

  const openEditTemplatePopup = id => {
    dispatch(getBatchMessageTemplateRequest({
      id,
      onSuccess: data => {
        dispatch(openBatchMessageTemplate({ edit: true, ...data }));
      }
    }));
  };

  const removeTemplate = id => {
    dispatch(removeBatchMessageTemplateRequest({
      id,
      onSuccess: () => {
        setConfirmDelete({ open: false, item: null });
        setMessage('Template successfully removed');
      }
    }));
  };

  return (
    <div className="batchMessageTemplates">
      <div className="batchMessageTemplates-header">
        <p className="batchMessageTemplates-header__title">Templates</p>
        <ActionButton
          variant="text"
          startIcon={<AddIcon />}
          title="Add template"
          onClick={openTemplatePopup}
        />
      </div>
      <div className="batchMessageTemplates-list">
        {(isEmpty(templates) && loading) && <Loader />}
        <InfiniteScroll
          dataLength={templates.length}
          next={loadMore}
          hasMore={next}
          loader={!(isEmpty(templates) && loading) && <Loader />}
          className="templatesList scrollbar"
          height={490}
        >
          {templates.map(template => {

            return (
              <div className="templatesList-item" key={template.id}>
                <div className="templatesList-item__info">
                  <Tooltip title={template.title}>
                    <p className="templatesList-item__title">{template.title}</p>
                  </Tooltip>
                  <Tooltip title={template.content}>
                    <p className="templatesList-item__message">{template.content}</p>
                  </Tooltip>
                </div>
                <div className="templatesList-item__buttons">
                  <ActionButton title="Insert" onClick={() => insertTemplate(template.content)} />
                  <IconButton
                    className="more"
                    onClick={e => openTemplateMenu(e.currentTarget, template)}
                  >
                    <MoreIcon />
                  </IconButton>
                </div>
              </div>
            );
          })}
        </InfiniteScroll>
      </div>
      <ActionMenu
        className="templateMenu"
        anchorEl={templateMenu.anchorEl}
        handleClose={closeTemplateMenu}
        actions={[
          { title: 'Edit', action: () => openEditTemplatePopup(templateMenu.item.id) },
          { title: 'Remove', action: () => setConfirmDelete({ open: true, item: templateMenu.item }) }
        ]}
      />
      <ConfirmActionPopup
        type="remove"
        open={confirmDelete.open}
        confirmAction={item => removeTemplate(item.id)}
        cancelAction={() => setConfirmDelete({ open: false, item: null })}
        object={confirmDelete.item}
        objectName="template"
      />
    </div>
  );
};

BatchMessageTemplates.propTypes = {
  insertTemplate: PropTypes.func
};

export default BatchMessageTemplates;
